import { Component, OnInit, ViewChild } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';

import { GlobalBlock } from '../../models/content';
import { ModalService } from '../../services/modal.service';
import { pageBlockRequest } from './block-preview/block-preview-request';
import { BlockPreviewSettingsService } from './block-preview/block-preview-settings.service';
import { BlockPreviewSource, SectionsEditorSource } from './block-preview/block-preview-source';
import { ContentSectionsEditorComponent } from './content-sections-editor.component';
import { GlobalBlockUse, globalBlockProblem } from './global-blocks';

/** What the list (or a page's sections editor) hands the dialog. */
export interface GlobalBlockEditorData {
  block: GlobalBlock;
  isNew: boolean;
  /** Every OTHER live global block: the name must differ from theirs. */
  others: GlobalBlock[];
  /** Where it is placed (pages, templates, suburb pages), for the "used on" line. */
  usedOn: GlobalBlockUse[];
  /** Performs the save; the dialog stays open (Save spinning) until it answers. */
  save: (block: GlobalBlock) => Observable<unknown>;
}

/**
 * Global block editor (#217): a name around the shared sections editor in its 'global' mode (no hero, no global block
 * inside). Opened from Website → Global blocks, and from a page's "Edit global block" and "Save as global block".
 */
@Component({
  selector: 'app-content-global-block',
  templateUrl: './content-global-block.component.html',
})
export class ContentGlobalBlockComponent implements OnInit {
  block!: GlobalBlock;
  isNew = false;
  name = '';
  usedOn: GlobalBlockUse[] = [];
  saving = false;
  closing = false;
  lastSavedAt: Date | null = null;

  @ViewChild(ContentSectionsEditorComponent, { static: true })
  sectionsEditor!: ContentSectionsEditorComponent;

  /** A global block can sit on any page: it previews at the home page's address. */
  previewSource!: BlockPreviewSource;

  private data!: GlobalBlockEditorData;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public preview: BlockPreviewSettingsService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.data = this.config.data;
    this.block = this.data.block;
    this.isNew = !!this.data.isNew;
    this.name = this.block.name ?? '';
    this.usedOn = this.data.usedOn ?? [];
    this.sectionsEditor.initFrom(this.block.sections);
    this.previewSource = new SectionsEditorSource(this.sectionsEditor, (sections, active) => pageBlockRequest('/', sections, active));
  }

  get usedOnText(): string {
    return this.usedOn.map((u) => u.label).join(', ');
  }

  onSave(close: boolean): void {
    const problem =
      globalBlockProblem(this.name, this.sectionsEditor.sections, this.data.others ?? []) ??
      // Validated as published: a global block shows on every page that places it, published or not.
      this.sectionsEditor.validate(true);
    if (problem) {
      this.modalService.generic({
        title: "This global block can't be saved yet",
        copy: [problem],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }
    Object.assign(this.block, { name: this.name.trim(), sections: this.sectionsEditor.toMap() });

    this.saving = true;
    this.closing = close;
    this.data.save(this.block).subscribe({
      next: () => {
        if (close) {
          this.ref.close({ saved: true, block: this.block });
          return;
        }
        this.saving = false;
        this.isNew = false;
        this.lastSavedAt = new Date();
      },
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }
}
